import { useState } from "react";
import { Button, Collapse, Dialog, Divider, useTheme } from "@mui/material";
import { DialogActions, FormattedDialogTitle, IconDialogContent } from "../components/dialogs";
import { defineMessage, FormattedMessage } from "react-intl";
import { InfoIcon } from "./icons";
import { BackendError, BackendErrorCode } from "../types";

const backendErrorMessages: Record<BackendErrorCode | "invalid_session", ReturnType<typeof defineMessage>> = {
    cant_reach_heimdall: defineMessage({
        id: "backendError.codes.cant_reach_heimdall",
        defaultMessage: "Heimdall is not available.",
    }),
    expired_access_token: defineMessage({
        id: "backendError.codes.expired_access_token",
        defaultMessage: "The session has expired.",
    }),
    invalid_session: defineMessage({ id: "backendError.codes.invalid_session", defaultMessage: "Invalid session." }),
    not_authorized: defineMessage({
        id: "backendError.codes.not_authorized",
        defaultMessage: "You don't have enough permissions to use this function.",
    }),
    unknown: defineMessage({ id: "backendError.codes.unknown", defaultMessage: "Unexpected error." }),
    unknown_access_token: defineMessage({
        id: "backendError.codes.unknown_access_token",
        defaultMessage: "Unknown authentication data",
    }),
    unknown_invitation: defineMessage({
        id: "backendError.codes.unknown_invitation",
        defaultMessage: "Unknown invitation",
    }),
    unreachable: defineMessage({
        id: "backendError.codes.unreachable",
        defaultMessage: "Network error: the backend is unreachable!",
    }),
};

export type Props = {
    backendError: BackendError;
};

export default (props: Props) => {
    const [showDetails, setShowDetails] = useState(false);

    const show = props.backendError !== null;

    return (
        <Dialog open={show}>
            <FormattedDialogTitle id={defineMessage({ id: "backendError.title", defaultMessage: "Backend error!" })} />
            <IconDialogContent icon="exclamation-triangle" iconColor={useTheme().palette.secondary.main}>
                <FormattedMessage {...backendErrorMessages[props.backendError?.code ?? "unknown"]} />
                {Boolean(props.backendError?.details) && (
                    <>
                        <InfoIcon
                            fixedWidth
                            onClick={() => setShowDetails(!showDetails)}
                            style={{ marginLeft: "0.3em", cursor: "pointer" }}
                        />
                        <Collapse in={showDetails}>
                            <Divider style={{ margin: "0.5em 0" }} />
                            <pre
                                style={{
                                    maxWidth: 400,
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "break-word",
                                }}
                            >
                                {props.backendError?.details}
                            </pre>
                        </Collapse>
                    </>
                )}
            </IconDialogContent>
            <DialogActions>
                <Button onClick={() => window.location.reload()} variant="text">
                    <FormattedMessage id="backendError.reload" defaultMessage="Reload the page" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};
